import EmberService, { service } from '@ember/service';

import type { APIData, Service } from 'webapp-types';
import { AccountSetupFeatureType } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';

import AccountPopupMissingFeaturesSetupComponent from 'webapp/components/account/popup-missing-features-setup';

// Introduce Account setup (missing features) popup for customers
// https://app.asana.com/0/527984279702626/1203063415208597/f

export interface FeatureData {
	id: AccountSetupFeatureType;
	order: number;
	isAvailable: boolean;
	usageCount: number;
}

export default class FlowAccountMissingFeaturesSetupService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;

	private get featuresData() {
		return [
			{
				id: AccountSetupFeatureType.SOURCES,
				order: 1,
				isAvailable: true,
			},
			{
				id: AccountSetupFeatureType.DATABOARDS,
				order: 2,
				isAvailable: true,
			},
			{
				id: AccountSetupFeatureType.CLIENTS,
				order: 3,
				isAvailable: this.accountService.isAgency,
			},
			{
				id: AccountSetupFeatureType.REPORTS,
				order: 4,
				isAvailable: this.billingService.reportsBasicEnabled,
			},
			{
				id: AccountSetupFeatureType.BOARD_LOOPS,
				order: 5,
				isAvailable: this.mmBoardsService.normalBoardModels.length >= 2,
			},
			{
				id: AccountSetupFeatureType.CUSTOM_METRICS,
				order: 6,
				isAvailable: this.billingService.qbEnabled,
			},
			{
				id: AccountSetupFeatureType.DATA_CALCULATIONS,
				order: 7,
				isAvailable: this.billingService.scriptsEnabled,
			},
			{
				id: AccountSetupFeatureType.GOALS,
				order: 8,
				isAvailable: this.billingService.features.goalsEnabled,
			},
			{
				id: AccountSetupFeatureType.SNAPSHOTS,
				order: 9,
				isAvailable: true,
			},
			{
				id: AccountSetupFeatureType.SCORECARDS,
				order: 10,
				isAvailable: true,
			},
			{
				id: AccountSetupFeatureType.METRIC_DIGESTS,
				order: 11,
				isAvailable: true,
			},
			{
				id: AccountSetupFeatureType.ALERTS,
				order: 12,
				isAvailable: this.billingService.features.alertsEnabled,
			},
			{
				id: AccountSetupFeatureType.WHITE_LABELING,
				order: 13,
				isAvailable: this.billingService.whiteLabelingEnabled,
			},
		];
	}

	private getFeatureCount(
		features: APIData.Account.Check.FeatureCount.FeatureItem[],
		featureId: AccountSetupFeatureType,
	): number {
		return features.find((item) => item.featureId === featureId)?.featureUsageCount || 0;
	}

	private openAccountMissingFeaturesSetupPopup(features: FeatureData[]): void {
		this.popupService
			.createModal(AccountPopupMissingFeaturesSetupComponent, {
				popupClass: 'popup-type-account-missing-features-setup wizard',
			})
			.open({ features });
	}

	checkToOpenPopup(): void {
		if (this.billingService.activePlan?.isZeroPayment) {
			return;
		}

		if (this.currentUserService.hasDataboxEmail) {
			return;
		}

		if (this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins) {
			return;
		}

		if (this.currentUserService.isAgencyUserInClientSpace) {
			return;
		}

		if (this.popupService.anyPopupsOpen) {
			return;
		}

		this.requestService
			.api<APIData.Account.Check.FeatureCount.Response>('GET', '/account/check/feature-count')
			.then((response) => {
				const { isEligible: isEligibleToShowPopup, data } = response;

				if (isEligibleToShowPopup) {
					const maxShownItemsCount = 3;

					const recommendedFeatures = this.featuresData
						.map((feature) => {
							return {
								...feature,
								usageCount: this.getFeatureCount(data, feature.id),
							};
						})
						.filter((item) => !item.usageCount && item.isAvailable);

					const topRecommendedFeatures = sortBy(recommendedFeatures, 'order').splice(
						0,
						maxShownItemsCount,
					);

					if (
						!this.popupService.anyPopupsOpen &&
						topRecommendedFeatures.length === maxShownItemsCount
					) {
						this.openAccountMissingFeaturesSetupPopup(topRecommendedFeatures);
					}
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
}

declare module '@ember/service' {
	interface Registry {
		'flow/account-missing-features-setup': FlowAccountMissingFeaturesSetupService;
	}
}
